import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Search as SearchIcon, X, BadgeCheck, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useDebounce } from '../hooks/useDebounce';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SearchPage = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [recentSearches, setRecentSearches] = useState([]);
    const debouncedQuery = useDebounce(query, 300);
    const navigate = useNavigate();

    useEffect(() => {
        // Load recent searches from localStorage
        const saved = localStorage.getItem('recentSearches');
        if (saved) {
            setRecentSearches(JSON.parse(saved));
        }
    }, []);

    useEffect(() => {
        if (debouncedQuery.trim()) {
            searchUsers();
        } else {
            setResults([]);
        }
    }, [debouncedQuery]);

    const searchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(debouncedQuery)}`);
            setResults(response.data);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUserClick = (user) => {
        // Save to recent searches
        const updatedRecent = [
            user,
            ...recentSearches.filter(u => u.id !== user.id)
        ].slice(0, 5);
        setRecentSearches(updatedRecent);
        localStorage.setItem('recentSearches', JSON.stringify(updatedRecent));
        
        navigate(`/profile/${user.id}`);
    };

    const clearRecentSearches = () => {
        setRecentSearches([]);
        localStorage.removeItem('recentSearches');
    };

    return (
        <div className="h-full flex flex-col bg-black" data-testid="search-page">
            {/* Search Header */}
            <div className="flex-shrink-0 glass border-b border-white/10">
                <div className="max-w-md mx-auto px-4 py-3">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                            data-testid="back-button"
                        >
                            <ChevronLeft className="w-6 h-6 text-white" />
                        </button>
                        <div className="flex-1 relative">
                            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search users..."
                                className="w-full bg-surface border-border text-white placeholder:text-white/40 pl-10 pr-10 h-11"
                                autoFocus
                                data-testid="search-input"
                            />
                            {query && (
                                <button
                                    onClick={() => setQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2"
                                >
                                    <X className="w-5 h-5 text-white/40 hover:text-white" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
                <div className="max-w-md mx-auto px-4 py-4 pb-8">
                {/* Loading */}
                {loading && (
                    <div className="flex justify-center py-8">
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                )}

                {/* Search Results */}
                {!loading && query && results.length > 0 && (
                    <div className="space-y-2" data-testid="search-results">
                        {results.map((user, index) => (
                            <motion.button
                                key={user.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                onClick={() => handleUserClick(user)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors"
                                data-testid={`search-result-${user.id}`}
                            >
                                <Avatar className="w-12 h-12">
                                    <AvatarImage src={user.avatar} alt={user.username} />
                                    <AvatarFallback className="bg-primary/20 text-primary">
                                        {user.username?.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 text-left">
                                    <div className="flex items-center gap-1">
                                        <span className="text-white font-medium">{user.username}</span>
                                        {user.is_verified && (
                                            <BadgeCheck className="w-4 h-4 text-primary fill-primary" />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 text-white/50 text-sm">
                                        {user.role && <span className="capitalize">{user.role}</span>}
                                        {user.region && <span>• {user.region}</span>}
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                )}

                {/* No Results */}
                {!loading && query && results.length === 0 && (
                    <div className="py-12 text-center">
                        <p className="text-white/60">No users found for "{query}"</p>
                    </div>
                )}

                {/* Recent Searches */}
                {!query && recentSearches.length > 0 && (
                    <div data-testid="recent-searches">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-white font-medium">Recent</span>
                            <button
                                onClick={clearRecentSearches}
                                className="text-primary text-sm font-medium"
                            >
                                Clear all
                            </button>
                        </div>
                        <div className="space-y-2">
                            {recentSearches.map((user) => (
                                <button
                                    key={user.id}
                                    onClick={() => handleUserClick(user)}
                                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors"
                                >
                                    <Avatar className="w-10 h-10">
                                        <AvatarImage src={user.avatar} alt={user.username} />
                                        <AvatarFallback className="bg-primary/20 text-primary text-sm">
                                            {user.username?.charAt(0).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="text-white">{user.username}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {!query && recentSearches.length === 0 && (
                    <div className="py-12 text-center">
                        <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-4">
                            <SearchIcon className="w-8 h-8 text-white/40" />
                        </div>
                        <p className="text-white/60">Search for users</p>
                        <p className="text-white/40 text-sm mt-1">Find players and fans in the community</p>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

export default SearchPage;
